import { useEffect, useState } from "react";
import api, { formatDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { MessageSquare, Send, History } from "lucide-react";
import { toast } from "sonner";

const templateLabels = {
  due_soon: "Fee Due Soon (7 days before)",
  due_today: "Fee Due Today",
  overdue: "Overdue Fee Notice",
  documents_pending: "Documents Pending",
  custom: "Custom Message",
};

export default function Reminders() {
  const [students, setStudents] = useState([]);
  const [templates, setTemplates] = useState({});
  const [log, setLog] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [templateType, setTemplateType] = useState("overdue");
  const [customMessage, setCustomMessage] = useState("");
  const [editTemplate, setEditTemplate] = useState("");
  const [filter, setFilter] = useState("overdue");
  const [twilio, setTwilio] = useState(null);
  const [running, setRunning] = useState(false);

  const load = async () => {
    const [s, t, l, tw] = await Promise.all([
      api.get("/students"),
      api.get("/reminders/templates"),
      api.get("/reminders/log"),
      api.get("/reminders/twilio-status").catch(() => ({ data: null })),
    ]);
    setStudents(s.data);
    setTemplates(t.data);
    setLog(l.data);
    setTwilio(tw.data);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { setEditTemplate(templates[templateType] || ""); }, [templateType, templates]);

  const filteredStudents = students.filter((s) => {
    if (filter === "all") return true;
    if (filter === "overdue") return s.fee_summary.status === "overdue";
    if (filter === "partial") return s.fee_summary.status === "partial";
    if (filter === "active") return s.status === "active";
    return true;
  });

  const toggleAll = () => {
    if (selected.size === filteredStudents.length) setSelected(new Set());
    else setSelected(new Set(filteredStudents.map((s) => s.id)));
  };
  const toggleOne = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };

  const send = async () => {
    if (selected.size === 0) return toast.error("Select at least 1 student");
    try {
      const { data } = await api.post("/reminders/send", {
        student_ids: [...selected],
        template_type: templateType,
        custom_message: customMessage,
      });
      toast.success(`${data.count} reminder(s) sent`);
      setSelected(new Set());
      load();
    } catch {
      toast.error("Failed to send");
    }
  };

  const saveTemplate = async () => {
    try {
      await api.put("/reminders/templates", { template_type: templateType, message: editTemplate });
      toast.success("Template saved");
      load();
    } catch {
      toast.error("Failed to save template");
    }
  };

  const runAutoNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/reminders/run-auto");
      const s = data.summary || {};
      const sent = (s.sent?.due_soon || 0) + (s.sent?.due_today || 0) + (s.sent?.overdue || 0);
      toast.success(`Auto-cron ran: scanned ${s.scanned}, sent ${sent}, failed ${s.failed || 0}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to run auto-cron");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-[1400px] mx-auto" data-testid="reminders-page">
      <div className="mb-8">
        <div className="label-caps mb-2 flex items-center gap-2"><MessageSquare size={12} /> Communication</div>
        <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: "Chivo" }}>WhatsApp Reminders</h1>
        <p className="text-slate-500 mt-1">
          Send fee and document reminders via Twilio WhatsApp.
        </p>
      </div>

      {/* Twilio status card */}
      {twilio && (
        <Card className="p-4 mb-6" data-testid="twilio-status-card">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <div className={`h-2 w-2 rounded-full ${twilio.configured ? "bg-emerald-500" : "bg-red-500"}`} />
                <div className="font-semibold">
                  Twilio {twilio.configured ? "Connected" : "Not Configured"}
                </div>
                {twilio.mode && (
                  <Badge variant="outline" className={twilio.mode === "sandbox" ? "border-amber-300 text-amber-700 bg-amber-50" : "border-emerald-300 text-emerald-700 bg-emerald-50"}>
                    {twilio.mode === "sandbox" ? "Sandbox" : "Business Sender"}
                  </Badge>
                )}
              </div>
              <div className="text-xs text-slate-500 font-mono mt-1">From: {twilio.from || "—"}</div>
              {twilio.sandbox_note && (
                <div className="text-xs mt-2 text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                  ⚠ Sandbox mode: {twilio.sandbox_note}
                </div>
              )}
            </div>
            <Button
              variant="outline"
              onClick={runAutoNow}
              disabled={running || !twilio.configured}
              data-testid="run-auto-cron"
              className="whitespace-nowrap"
            >
              {running ? "Running…" : "Run Auto-Cron Now"}
            </Button>
          </div>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left: Audience */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold" style={{ fontFamily: "Chivo" }}>1. Select Audience</h3>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-40 h-9" data-testid="audience-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="partial">Partial Paid</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 pb-3 border-b">
            <Checkbox checked={selected.size === filteredStudents.length && filteredStudents.length > 0} onCheckedChange={toggleAll} data-testid="select-all" />
            <span className="text-sm font-medium">Select all ({filteredStudents.length})</span>
            <span className="ml-auto text-xs text-slate-500">{selected.size} selected</span>
          </div>
          <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
            {filteredStudents.map((s) => (
              <label key={s.id} className="flex items-center gap-3 py-3 cursor-pointer hover:bg-slate-50 px-2 rounded">
                <Checkbox checked={selected.has(s.id)} onCheckedChange={() => toggleOne(s.id)} data-testid={`select-${s.id}`} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{s.name}</div>
                  <div className="text-xs text-slate-500 font-mono">{s.phone}</div>
                </div>
                <Badge variant="outline" className={`status-${s.fee_summary.status}`}>{s.fee_summary.status}</Badge>
              </label>
            ))}
            {filteredStudents.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">No students match this filter</div>}
          </div>
        </Card>

        {/* Right: Template + send */}
        <Card className="p-6">
          <h3 className="font-semibold mb-4" style={{ fontFamily: "Chivo" }}>2. Choose Template</h3>
          <Select value={templateType} onValueChange={setTemplateType}>
            <SelectTrigger className="h-11 mb-3" data-testid="template-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(templateLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Textarea
            data-testid="template-message"
            value={editTemplate}
            onChange={(e) => setEditTemplate(e.target.value)}
            rows={6}
            className="font-mono text-sm"
          />
          <p className="text-xs text-slate-500 mt-2">
            Placeholders: <code>{"{name}"}</code> <code>{"{amount}"}</code> <code>{"{due_date}"}</code> <code>{"{documents}"}</code> <code>{"{message}"}</code>
          </p>
          {templateType === "custom" && (
            <Textarea
              placeholder="Type custom message here..."
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              rows={3}
              className="mt-3"
              data-testid="custom-message"
            />
          )}
          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={saveTemplate} data-testid="save-template">Save Template</Button>
            <Button className="flex-1 bg-slate-900 hover:bg-slate-800 h-11" onClick={send} data-testid="send-reminders-button" disabled={selected.size === 0}>
              <Send size={16} className="mr-2" /> Send to {selected.size}
            </Button>
          </div>
        </Card>
      </div>

      {/* Reminder log */}
      <Card className="p-6 mt-6" data-testid="reminder-log">
        <div className="flex items-center gap-2 mb-4">
          <History size={18} className="text-slate-500" />
          <h3 className="font-semibold" style={{ fontFamily: "Chivo" }}>Reminder Log</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr className="text-left">
                <th className="px-4 py-3 label-caps">Student</th>
                <th className="px-4 py-3 label-caps">Phone</th>
                <th className="px-4 py-3 label-caps">Type</th>
                <th className="px-4 py-3 label-caps">Message</th>
                <th className="px-4 py-3 label-caps">Sent At</th>
                <th className="px-4 py-3 label-caps">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {log.map((r) => (
                <tr key={r.id} data-testid={`log-row-${r.id}`}>
                  <td className="px-4 py-3 font-medium">{r.student_name}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.phone}</td>
                  <td className="px-4 py-3"><Badge variant="outline">{r.template_type}</Badge></td>
                  <td className="px-4 py-3 max-w-md truncate text-xs text-slate-600">{r.message}</td>
                  <td className="px-4 py-3 text-xs">{formatDate(r.sent_at)}</td>
                  <td className="px-4 py-3"><Badge variant="outline" className="status-paid">{r.status}</Badge></td>
                </tr>
              ))}
              {log.length === 0 && <tr><td colSpan="6" className="px-4 py-8 text-center text-slate-500">No reminders sent yet</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
